/**
 * Typography used in theme
 * @param {JsonObject} theme theme customization object
 */

// The scale ran from 24px down to 8px: h6 was 0.5rem, h5 0.6rem, body1 0.7rem — all
// below the legibility floor for a workstation UI, which is why individual pages kept
// overriding it inline. `body2` set no fontSize at all, so it fell through to MUI's
// 16px default and rendered LARGER than h3.
//
// Rebuilt on a 14px base at a ~1.15-1.2 ratio. Product UI needs more type roles than a
// marketing page, so the steps stay close together; exaggerated contrast just makes
// noise. Fixed rem values, not clamp() — admins view at a consistent DPI and a fluid
// heading that shrinks inside a panel looks worse, not better.
export default function themeTypography(theme) {
  return {
    fontFamily: theme?.customization?.fontFamily,
    h6: {
      fontWeight: 600,
      color: theme.heading,
      fontSize: '0.875rem' // 14px — field group labels
    },
    h5: {
      fontSize: '0.9375rem', // 15px — section labels
      color: theme.heading,
      fontWeight: 600
    },
    h4: {
      fontSize: '1.0625rem', // 17px — card titles
      color: theme.heading,
      fontWeight: 600
    },
    h3: {
      fontSize: '1.25rem', // 20px
      color: theme.heading,
      fontWeight: 600
    },
    h2: {
      fontSize: '1.5rem', // 24px
      color: theme.heading,
      fontWeight: 700,
      letterSpacing: '-0.015em'
    },
    h1: {
      fontSize: '1.875rem', // 30px — page titles
      color: theme.heading,
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2
    },
    subtitle1: {
      fontSize: '0.875rem', // 14px
      fontWeight: 500,
      color: theme.textDark
    },
    subtitle2: {
      fontSize: '0.8125rem', // 13px
      fontWeight: 500,
      color: theme.darkTextSecondary
    },
    caption: {
      fontSize: '0.75rem', // 12px — timestamps, helper text, counts
      color: theme.darkTextSecondary,
      fontWeight: 400
    },
    body1: {
      fontSize: '0.875rem', // 14px — table cells, form values, message text
      fontWeight: 400,
      lineHeight: 1.55
    },
    body2: {
      fontSize: '0.8125rem', // 13px — was unset, so it inherited MUI's 16px default
      letterSpacing: '0em',
      fontWeight: 400,
      lineHeight: 1.55,
      color: theme.darkTextPrimary
    },
    button: {
      textTransform: 'capitalize',
      fontSize: '0.875rem',
      fontWeight: 500
    },
    customInput: {
      marginTop: 1,
      marginBottom: 1,
      '& > label': {
        top: 23,
        left: 0,
        color: theme.grey500,
        '&[data-shrink="false"]': {
          top: 5
        }
      },
      '& > div > input': {
        padding: '30.5px 14px 11.5px !important'
      },
      '& legend': {
        display: 'none'
      },
      '& fieldset': {
        top: 0
      }
    },
    mainContent: {
      backgroundColor: theme.background,
      width: '100%',
      minHeight: 'calc(100vh - 88px)',
      flexGrow: 1,
      // 15px against MUI's 8pt scale put everything downstream a half-step out of
      // rhythm. 24px is 3 units and matches the card padding it sits next to.
      padding: '24px',
      marginTop: '88px',
      marginRight: '16px',
      borderRadius: `${theme?.customization?.borderRadius}px`
    },
    // Sidebar group headings. These are labels, not headings — uppercase with tracking
    // reads as a category marker at a size that stays legible, where 0.7rem sentence
    // case just looked like undersized body copy.
    menuCaption: {
      fontSize: '0.6875rem', // 11px
      fontWeight: 640,
      color: theme.darkTextSecondary,
      padding: '6px 8px',
      textTransform: 'uppercase',
      letterSpacing: '0.09em',
      marginTop: '14px'
    },
    subMenuCaption: {
      fontSize: '0.75rem', // 12px — was 0.5rem (8px)
      fontWeight: 500,
      color: theme.darkTextSecondary,
      textTransform: 'capitalize'
    },
    commonAvatar: {
      cursor: 'pointer',
      borderRadius: '8px'
    },
    // These double as the header's icon-button targets. At 18/24/28px they sat well
    // under the 44px comfortable-touch floor and were awkward to hit on a laptop
    // trackpad, let alone a phone.
    smallAvatar: {
      width: '22px',
      height: '22px',
      fontSize: '0.8rem'
    },
    mediumAvatar: {
      width: '32px',
      height: '32px',
      fontSize: '1rem'
    },
    largeAvatar: {
      width: '36px',
      height: '36px',
      fontSize: '1.2rem'
    }
  };
}
